import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography, Button, TextField, Switch, Autocomplete } from '@mui/material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { scrollAreaSx } from '../../utils/scrollSx';
import { RootState } from '../../redux/store';
import axios from '../../interceptors/axios';
import { TimeSettings, publishTimeSettings } from '../../hooks/useTimeSettings';
import { nextDstTransitions, formatOffset } from '../../utils/timezone';
import { usePageHeader } from '../../context/PageHeaderContext';

interface MediaSettings {
  media_default_image_scale: string;
  media_default_video_scale: string;
  media_transcode_8bit: boolean;
  media_auto_resize: boolean;
  media_auto_resize_resolution: string;
}


const SCALE_MODES = [
  { value: 'fill',    label: 'Fill'    },
  { value: 'fit',     label: 'Fit'     },
  { value: 'stretch', label: 'Stretch' },
] as const;

const RESOLUTIONS = ['720p', '2k', '4k'] as const;

const FALLBACK_TIMEZONES = [
  'Europe/Kyiv', 'Europe/London', 'Europe/Berlin', 'Europe/Warsaw',
  'Europe/Prague', 'Europe/Chisinau', 'UTC',
];

const TIMEZONES: string[] = (() => {
  const supported = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf;
  try {
    return supported ? supported('timeZone') : FALLBACK_TIMEZONES;
  } catch {
    return FALLBACK_TIMEZONES;
  }
})();

/** Current wall-clock time in the given zone — shows the operator what they picked. */
function zonePreview(timezone: string): string {
  try {
    return new Intl.DateTimeFormat('uk-UA', {
      timeZone: timezone, hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit',
    }).format(new Date());
  } catch {
    return '—';
  }
}

function ScaleButtons({ value, onChange, disabled }: { value: string; onChange: (v: string) => void; disabled?: boolean }) {
  return (
    <Box sx={{ display: 'flex', gap: '4px' }}>
      {SCALE_MODES.map(m => (
        <Box
          key={m.value}
          component="button"
          onClick={() => !disabled && onChange(m.value)}
          sx={{
            flex: 1, py: '5px', fontSize: 11, fontWeight: 600, fontFamily: 'inherit', border: 'none',
            borderRadius: '7px', cursor: disabled ? 'default' : 'pointer', transition: 'all .12s',
            background: value === m.value ? 'rgba(99,102,241,.3)' : 'var(--glass-hover)',
            color: value === m.value ? 'var(--accent-light)' : disabled ? 'var(--text-disabled)' : 'var(--text-secondary)',
            opacity: disabled ? 0.5 : 1,
          }}
        >
          {m.label}
        </Box>
      ))}
    </Box>
  );
}

const SettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const auth = useSelector((s: RootState) => s.auth.value);
  const { user } = useSelector((s: RootState) => s.auth);
  const { setSaveConfig } = usePageHeader();

  const [mediaSettings, setMediaSettings] = useState<MediaSettings>({
    media_default_image_scale: 'fill',
    media_default_video_scale: 'fill',
    media_transcode_8bit: false,
    media_auto_resize: false,
    media_auto_resize_resolution: '2k',
  });
  const [mediaDirty, setMediaDirty] = useState(false);
  const [mediaSaving, setMediaSaving] = useState(false);
  const [mediaSaved, setMediaSaved] = useState(false);
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [timeSettings, setTimeSettings] = useState<TimeSettings>({ timezone: 'Europe/Kyiv', dst_mode: 'auto' });
  const [timeDirty, setTimeDirty] = useState(false);


  const fetchMediaSettings = useCallback(async () => {
    try {
      const { data } = await axios.get<MediaSettings>('/company/media-settings');
      setMediaSettings(data);
      setMediaDirty(false);
    } catch {}
  }, []);

  const fetchTimeSettings = useCallback(async () => {
    try {
      const { data } = await axios.get<TimeSettings>('/company/time-settings');
      setTimeSettings(data);
      setTimeDirty(false);
    } catch {}
  }, []);

  useEffect(() => { if (auth) fetchMediaSettings(); }, [auth, fetchMediaSettings]);
  useEffect(() => { if (auth) fetchTimeSettings(); }, [auth, fetchTimeSettings]);

  const updateMedia = (patch: Partial<MediaSettings>) => {
    setMediaSettings(prev => ({ ...prev, ...patch }));
    setMediaDirty(true);
    setMediaSaved(false);
  };

  const updateTime = (patch: Partial<TimeSettings>) => {
    setTimeSettings(prev => ({ ...prev, ...patch }));
    setTimeDirty(true);
    setMediaSaved(false);
  };

  const dirty = mediaDirty || timeDirty;

  // Register save state in topbar
  useEffect(() => {
    setSaveConfig({
      dirty,
      saving: mediaSaving,
      saved: mediaSaved,
      onSave: saveSettings,
    });
    return () => setSaveConfig(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, mediaSaving, mediaSaved]);

  const dstTransitions = React.useMemo(
    () => nextDstTransitions(timeSettings.timezone, 2),
    [timeSettings.timezone],
  );

  const saveSettings = async () => {
    setMediaSaving(true);
    try {
      if (mediaDirty) await axios.patch('/company/media-settings', mediaSettings);
      if (timeDirty) {
        await axios.patch('/company/time-settings', timeSettings);
        publishTimeSettings(timeSettings);   // refresh the topbar clock at once
      }
      setMediaDirty(false);
      setTimeDirty(false);
      setMediaSaved(true);
      if (savedTimer.current) clearTimeout(savedTimer.current);
      savedTimer.current = setTimeout(() => setMediaSaved(false), 2000);
    } catch {}
    setMediaSaving(false);
  };

  return (
    <Box sx={{ flex: 1, overflowY: 'auto', p: '16px', ...scrollAreaSx }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '22px', maxWidth: 760 }}>

      {/* Media Settings */}
      <Box>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.06em', mb: '10px' }}>
          {t('settings.mediaSection')}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* Scale defaults — stacked vertically, buttons constrained to left */}
          <Box>
            <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-disabled)', mb: '8px' }}>
              {t('settings.imageScaleDefault')}
            </Typography>
            <Box sx={{ maxWidth: 280 }}>
              <ScaleButtons value={mediaSettings.media_default_image_scale} onChange={v => updateMedia({ media_default_image_scale: v })} />
            </Box>
          </Box>
          <Box sx={{ height: '1px', background: 'var(--glass-border)' }} />
          <Box>
            <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-disabled)', mb: '8px' }}>
              {t('settings.videoScaleDefault')}
            </Typography>
            <Box sx={{ maxWidth: 280 }}>
              <ScaleButtons value={mediaSettings.media_default_video_scale} onChange={v => updateMedia({ media_default_video_scale: v })} />
            </Box>
          </Box>

          <Box sx={{ height: '1px', background: 'var(--glass-border)' }} />

          {/* Video transcoding */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                {t('settings.transcode8bit')}
              </Typography>
              <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', mt: '2px' }}>
                {t('settings.transcode8bitHint')}
              </Typography>
            </Box>
            <Switch
              checked={mediaSettings.media_transcode_8bit}
              onChange={e => updateMedia({ media_transcode_8bit: e.target.checked })}
              sx={{
                flexShrink: 0,
                '& .MuiSwitch-switchBase.Mui-checked': { color: 'var(--accent)' },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'var(--accent)' },
              }}
            />
          </Box>

          <Box sx={{ height: '1px', background: 'var(--glass-border)' }} />

          {/* Auto-resize (disabled / future) */}
          <Box sx={{ opacity: 0.5, pointerEvents: 'none', userSelect: 'none' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', mb: '10px' }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                    {t('settings.autoResize')}
                  </Typography>
                  <Box sx={{ px: '7px', py: '2px', borderRadius: '5px', background: 'rgba(99,102,241,.2)', border: '1px solid rgba(99,102,241,.3)' }}>
                    <Typography sx={{ fontSize: 9, fontWeight: 700, color: 'var(--accent-light)', textTransform: 'uppercase', letterSpacing: '.08em' }}>
                      {t('settings.comingSoon')}
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', mt: '2px' }}>
                  {t('settings.autoResizeHint')}
                </Typography>
              </Box>
              <Switch checked={false} disabled />
            </Box>
            <Box sx={{ display: 'flex', gap: '6px' }}>
              {RESOLUTIONS.map(r => (
                <Box
                  key={r}
                  component="button"
                  sx={{
                    px: '14px', py: '5px', fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
                    border: '1px solid var(--glass-border)', borderRadius: '7px',
                    background: mediaSettings.media_auto_resize_resolution === r ? 'rgba(99,102,241,.3)' : 'transparent',
                    color: mediaSettings.media_auto_resize_resolution === r ? 'var(--accent-light)' : 'var(--text-secondary)',
                    cursor: 'default',
                  }}
                >
                  {r.toUpperCase()}
                </Box>
              ))}
            </Box>
          </Box>

        </Box>
      </Box>

      {/* Time Settings */}
      <Box sx={{ pt: '22px', borderTop: '1px solid var(--divider)' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.06em', mb: '10px' }}>
          {t('settings.timeSection')}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* Timezone */}
          <Box>
            <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-disabled)', mb: '8px' }}>
              {t('settings.timezone')}
            </Typography>
            <Autocomplete
              options={TIMEZONES}
              value={timeSettings.timezone}
              onChange={(_, v) => v && updateTime({ timezone: v })}
              disableClearable
              renderInput={params => (
                <TextField
                  {...params}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: 'var(--input-bg)', borderRadius: '8px', fontSize: 13,
                      color: 'var(--text-primary)',
                      '& fieldset': { borderColor: 'var(--input-border)' },
                      '&:hover fieldset': { borderColor: 'var(--input-border)' },
                      '&.Mui-focused fieldset': { borderColor: 'var(--accent)' },
                    },
                    '& .MuiSvgIcon-root': { color: 'var(--text-disabled)' },
                  }}
                />
              )}
            />
            <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', mt: '6px' }}>
              {t('settings.timezoneNow', { time: zonePreview(timeSettings.timezone) })}
            </Typography>
          </Box>

          <Box sx={{ height: '1px', background: 'var(--glass-border)' }} />

          {/* DST toggle */}
          <Box>
            <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-disabled)', mb: '8px' }}>
              {t('settings.dst')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
              <Typography sx={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                {timeSettings.dst_mode === 'auto' ? t('settings.dstAuto') : t('settings.dstOff')}
              </Typography>
              <Switch
                checked={timeSettings.dst_mode === 'auto'}
                onChange={e => updateTime({ dst_mode: e.target.checked ? 'auto' : 'off' })}
                sx={{
                  flexShrink: 0,
                  '& .MuiSwitch-switchBase.Mui-checked': { color: 'var(--accent)' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'var(--accent)' },
                }}
              />
            </Box>
          </Box>

          {/* Upcoming clock changes — so the operator knows when they happen */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-disabled)' }}>
              {t('settings.dstChanges')}
            </Typography>
            {timeSettings.dst_mode === 'off' ? (
              <Typography sx={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                {t('settings.dstDisabled')}
              </Typography>
            ) : dstTransitions.length === 0 ? (
              <Typography sx={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                {t('settings.dstNoChanges')}
              </Typography>
            ) : (
              dstTransitions.map(tr => (
                <Box key={tr.at.toISOString()} sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 600 }}>
                    {new Intl.DateTimeFormat('uk-UA', {
                      timeZone: timeSettings.timezone, day: '2-digit', month: 'long', year: 'numeric',
                      hour: '2-digit', minute: '2-digit',
                    }).format(tr.at)}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {tr.forward ? t('settings.dstForward') : t('settings.dstBackward')} · {formatOffset(tr.fromOffset)} → {formatOffset(tr.toOffset)}
                  </Typography>
                </Box>
              ))
            )}
          </Box>

          <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)' }}>
            {t('settings.timeNote')}
          </Typography>

        </Box>
      </Box>

      </Box>{/* end maxWidth wrapper */}
    </Box>
  );
};

export default SettingsPage;
